import katex from 'katex';

/**
 * Render LaTeX string to HTML using KaTeX
 */
const renderLatex = (latexStr) => {
  try {
    return katex.renderToString(latexStr, {
      throwOnError: false,
      displayMode: false,
      strict: false
    });
  } catch (e) {
    console.warn('KaTeX render error:', e);
    return latexStr;
  }
};

/**
 * Process plain text for LaTeX patterns
 * This handles text content only, not HTML tags
 */
const processTextForLatex = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  let processed = text;
  
  // 1. Square roots: √x, √(x+1)
  processed = processed.replace(/√\(([^)]+)\)/g, (match, content) => {
    const latex = renderLatex(`\\sqrt{${content}}`);
    return `<span class="math-inline">${latex}</span>`;
  });
  processed = processed.replace(/√([a-zA-Z0-9]+)/g, (match, content) => {
    const latex = renderLatex(`\\sqrt{${content}}`);
    return `<span class="math-inline">${latex}</span>`;
  });
  
  // 2. Fractions: 10/24, 35/16, etc.
  processed = processed.replace(/\b(\d+)\/(\d+)\b/g, (match, num, denom) => {
    const latex = renderLatex(`\\frac{${num}}{${denom}}`);
    return `<span class="math-inline">${latex}</span>`;
  });
  
  // 3. Algebraic fractions: x/y, a/b (only single letters)
  processed = processed.replace(/\b([a-zA-Z])\/([a-zA-Z])\b/g, (match, num, denom) => {
    const latex = renderLatex(`\\frac{${num}}{${denom}}`);
    return `<span class="math-inline">${latex}</span>`;
  });
  
  // 4. Explicit superscripts: x^4, y^2, etc.
  processed = processed.replace(/([a-zA-Z])(\^)(\d+)/g, (match, base, caret, exp) => {
    const latex = renderLatex(`${base}^{${exp}}`);
    return `<span class="math-inline">${latex}</span>`;
  });
  
  // 5. Complex expressions with explicit superscripts: cx^4y
  processed = processed.replace(/([a-zA-Z]+)(\^)(\d+)([a-zA-Z]*)/g, (match, prefix, caret, exp, suffix) => {
    // Already handled by pattern 4, but this catches multi-char prefixes
    const latex = renderLatex(`${prefix}^{${exp}}${suffix}`);
    return `<span class="math-inline">${latex}</span>`;
  });
  
  // 6. Implicit superscripts (NO caret): x2, x4, cx4y
  // ONLY if followed by math operators, spaces, or end of string
  processed = processed.replace(/([a-zA-Z]+)(\d)(?=[\s+\-×÷=<>()\[\]|,.]|$)/g, (match, base, digit) => {
    // Don't process if it's part of a larger number or word
    const latex = renderLatex(`${base}^{${digit}}`);
    return `<span class="math-inline">${latex}</span>`;
  });
  
  // 7. Subscripts: x_2, y_1, etc.
  processed = processed.replace(/([a-zA-Z])(_)(\d+)/g, (match, base, underscore, sub) => {
    const latex = renderLatex(`${base}_{${sub}}`);
    return `<span class="math-inline">${latex}</span>`;
  });
  
  // 8. Linear equations: y=-4x+2, y=4x-3
  processed = processed.replace(/([a-zA-Z])\s*=\s*([-−]?\d+)([a-zA-Z])([+\-−]\d+)/g, (match, leftVar, coef, rightVar, constant) => {
    const cleanCoef = coef.replace(/−/g, '-');
    const cleanConst = constant.replace(/−/g, '-').replace(/\+/g, '+');
    const latex = renderLatex(`${leftVar}=${cleanCoef}${rightVar}${cleanConst}`);
    return `<span class="math-inline">${latex}</span>`;
  });
  
  // 9. Equations: f(x)=x2-12x+30 → f(x)=x²-12x+30
  processed = processed.replace(/([a-zA-Z])\(([a-zA-Z])\)\s*=\s*([^<]+)/g, (match, func, var1, expr) => {
    // Don't process if already has math spans
    if (expr.includes('math-inline')) return match;
    
    // Process the expression for superscripts
    let processedExpr = expr.replace(/([a-zA-Z])(\d)(?=[\s+\-×÷=]|$)/g, (m, base, digit) => {
      return `${base}^{${digit}}`;
    });
    const latex = renderLatex(`${func}(${var1})=${processedExpr}`);
    return `<span class="math-inline">${latex}</span>`;
  });
  
  return processed;
};

/**
 * Check if content contains MathQuill markup
 * MathQuill uses its own HTML structure that should NOT be modified
 */
const containsMathQuill = (html) => {
  if (!html || typeof html !== 'string') return false;
  return /class="mq-math-mode"|class="mq-fraction"|class="mq-root-block"/i.test(html);
};

/**
 * Process HTML content with LaTeX in text nodes
 * CRITICAL: Do NOT process MathQuill-formatted content
 */
export const processMathContent = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') return '';
  
  // CRITICAL CHECK: If content has MathQuill markup, return as-is!
  // MathQuill handles its own rendering with CSS
  if (containsMathQuill(htmlContent)) {
    return htmlContent;
  }
  
  // If no HTML tags, process as plain text
  if (!/<[^>]+>/.test(htmlContent)) {
    return processTextForLatex(htmlContent);
  }
  
  try {
    // DOM-based processing for text nodes only
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${htmlContent}</div>`, 'text/html');
    const container = doc.body.firstChild;
    
    // Process all text nodes
    const processTextNodes = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Process text content for LaTeX
        const processedText = processTextForLatex(node.textContent);
        if (processedText !== node.textContent && processedText.includes('<span')) {
          // Replace text node with processed HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = processedText;
          while (tempDiv.firstChild) {
            node.parentNode.insertBefore(tempDiv.firstChild, node);
          }
          node.parentNode.removeChild(node);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Skip processing for img, script, style, and MathQuill tags
        if (!['IMG', 'SCRIPT', 'STYLE'].includes(node.tagName)) {
          // Process child nodes
          Array.from(node.childNodes).forEach(processTextNodes);
        }
      }
    };
    
    processTextNodes(container);
    
    // Return the processed HTML
    return container.innerHTML;
  } catch (e) {
    console.warn('Error processing math content:', e);
    // Fallback: return original content
    return htmlContent;
  }
};

/**
 * Create a component wrapper for rendering math content
 */
export const MathContent = ({ content }) => {
  if (!content) return null;
  
  const processedContent = processMathContent(content);
  
  return (
    <div dangerouslySetInnerHTML={{ __html: processedContent }} />
  );
};
